import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ONE_TAP_DEFAULT_PREFERENCES,
  fetchOneTapPreferences,
  saveOneTapPreferences,
} from './oneTapSchedule';
import { trackOneTapSchedulePreferencesSaved } from './userJourneyTracker';
import './OneTapScheduleModal.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const PLATFORM_ICONS = {
  facebook: `${PUBLIC_URL}/Facebook.png`,
  instagram: `${PUBLIC_URL}/instagram.svg`,
  linkedin: `${PUBLIC_URL}/linkedin.svg`,
  youtube: `${PUBLIC_URL}/YouTube.svg`,
};

const PLATFORMS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
];

const PRESET_TIMES = [
  { value: '07:00', label: '7 AM' },
  { value: '16:00', label: '4 PM' },
  { value: '22:00', label: '10 PM' },
];

const FREQUENCY_OPTIONS = [1, 2, 3];

function isPresetTime(value) {
  return PRESET_TIMES.some((preset) => preset.value === value);
}

function normalizeTimeSlots(slots, postsPerDay) {
  const unique = [];
  slots.forEach((slot) => {
    if (typeof slot === 'string' && /^\d{2}:\d{2}$/.test(slot) && !unique.includes(slot)) {
      unique.push(slot);
    }
  });
  return unique.slice(0, postsPerDay);
}

export default function OneTapScheduleModal({
  isOpen,
  onClose,
  accounts = [],
  userId = '',
  getFreshTokenRef,
  onSaved,
}) {
  const connectedAccounts = useMemo(
    () => accounts.filter((account) => account.status === 'active'),
    [accounts],
  );
  const connectedPlatformIds = useMemo(
    () => connectedAccounts.map((account) => account.platform),
    [connectedAccounts],
  );

  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [postsPerDay, setPostsPerDay] = useState(ONE_TAP_DEFAULT_PREFERENCES.postsPerDay);
  const [timeSlots, setTimeSlots] = useState(ONE_TAP_DEFAULT_PREFERENCES.timeSlots);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customTime, setCustomTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    async function loadPreferences() {
      const getToken = () => getFreshTokenRef?.current?.();
      const saved = getFreshTokenRef
        ? await fetchOneTapPreferences(getToken, userId)
        : null;
      if (cancelled) return;

      const initialPlatforms = saved?.platforms?.length
        ? saved.platforms.filter((id) => connectedPlatformIds.includes(id))
        : connectedPlatformIds;
      const initialPostsPerDay = saved?.postsPerDay || ONE_TAP_DEFAULT_PREFERENCES.postsPerDay;
      const initialTimeSlots = normalizeTimeSlots(
        saved?.timeSlots?.length ? saved.timeSlots : ONE_TAP_DEFAULT_PREFERENCES.timeSlots,
        initialPostsPerDay,
      );

      setSelectedPlatforms(initialPlatforms.length ? initialPlatforms : connectedPlatformIds);
      setEnabled(saved?.enabled !== false);
      setPostsPerDay(initialPostsPerDay);
      setTimeSlots(
        initialTimeSlots.length
          ? initialTimeSlots
          : ONE_TAP_DEFAULT_PREFERENCES.timeSlots.slice(0, initialPostsPerDay),
      );
      setShowCustomPicker(
        initialTimeSlots.some((slot) => !isPresetTime(slot)),
      );
      setCustomTime(
        initialTimeSlots.find((slot) => !isPresetTime(slot)) || '',
      );
      setError('');
      setSuccessMessage('');
      setSubmitting(false);
    }

    loadPreferences();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, connectedPlatformIds.join('|'), getFreshTokenRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, submitting, onClose]);

  const togglePlatform = (platformId) => {
    setSelectedPlatforms((current) =>
      current.includes(platformId)
        ? current.filter((id) => id !== platformId)
        : [...current, platformId],
    );
  };

  const handleFrequencyChange = (nextFrequency) => {
    setPostsPerDay(nextFrequency);
    setTimeSlots((current) => normalizeTimeSlots(current, nextFrequency));
    setShowCustomPicker(false);
    setCustomTime('');
    setError('');
  };

  const selectPresetTime = (value) => {
    setShowCustomPicker(false);
    setCustomTime('');
    if (postsPerDay === 1) {
      setTimeSlots([value]);
    } else {
      setTimeSlots((current) => {
        if (current.includes(value)) {
          return current.filter((slot) => slot !== value);
        }
        if (current.length >= postsPerDay) {
          return current;
        }
        return normalizeTimeSlots([...current, value], postsPerDay);
      });
    }
    setError('');
  };

  const handleCustomChipClick = () => {
    setShowCustomPicker(true);
    if (postsPerDay === 1) {
      const currentCustom = timeSlots.find((slot) => !isPresetTime(slot));
      setCustomTime(currentCustom || '');
      if (currentCustom) {
        setTimeSlots([currentCustom]);
      } else {
        setTimeSlots([]);
      }
    }
    setError('');
  };

  const handleAddCustomTime = () => {
    if (!customTime) {
      setError('Pick a custom time first.');
      return;
    }
    if (postsPerDay === 1) {
      setTimeSlots([customTime]);
      setError('');
      return;
    }
    if (timeSlots.includes(customTime)) {
      setError('That time is already selected.');
      return;
    }
    if (timeSlots.length >= postsPerDay) {
      setError(`You can only pick ${postsPerDay} time slots per day.`);
      return;
    }
    setTimeSlots((current) => normalizeTimeSlots([...current, customTime], postsPerDay));
    setCustomTime('');
    setShowCustomPicker(false);
    setError('');
  };

  const isCustomActive =
    showCustomPicker || timeSlots.some((slot) => !isPresetTime(slot));

  const handleSubmit = async () => {
    setError('');
    setSuccessMessage('');

    if (!enabled) {
      const preferences = {
        enabled: false,
        platforms: selectedPlatforms,
        postsPerDay,
        timeSlots: [...timeSlots].sort(),
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      };

      trackOneTapSchedulePreferencesSaved({
        enabled: false,
        platforms: selectedPlatforms.join(','),
        posts_per_day: postsPerDay,
      });

      setSubmitting(true);
      try {
        const getToken = () => getFreshTokenRef?.current?.();
        const saved = await saveOneTapPreferences(getToken, userId, preferences);
        onSaved?.(saved || preferences);
        setSuccessMessage('1-Tap schedule turned off.');
        setTimeout(() => onClose(), 900);
      } catch (err) {
        setError(err.message || 'Could not save preferences.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (selectedPlatforms.length === 0) {
      setError('Choose at least one platform.');
      return;
    }
    if (timeSlots.length !== postsPerDay) {
      setError(`Select ${postsPerDay} time slot${postsPerDay > 1 ? 's' : ''} for your daily schedule.`);
      return;
    }

    const preferences = {
      enabled: true,
      platforms: selectedPlatforms,
      postsPerDay,
      timeSlots: [...timeSlots].sort(),
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    };

    trackOneTapSchedulePreferencesSaved({
      enabled: true,
      platforms: selectedPlatforms.join(','),
      posts_per_day: postsPerDay,
      time_slots: timeSlots.join(','),
    });

    setSubmitting(true);
    try {
      const getToken = () => getFreshTokenRef?.current?.();
      const saved = await saveOneTapPreferences(getToken, userId, preferences);
      onSaved?.(saved || preferences);
      setSuccessMessage('Preferences saved.');
      setTimeout(() => onClose(), 900);
    } catch (err) {
      setError(err.message || 'Could not save preferences.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const slotsRemaining = postsPerDay - timeSlots.length;
  const canAddCustomTime =
    postsPerDay === 1 ? Boolean(customTime) : timeSlots.length < postsPerDay;

  return createPortal(
    <div
      className="one-tap-modal-overlay"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="one-tap-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="one-tap-schedule-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="one-tap-modal__header">
          <h2 id="one-tap-schedule-title" className="one-tap-modal__title">
            1-Tap schedule
          </h2>
          <button
            type="button"
            className="one-tap-modal__close"
            aria-label="Close"
            disabled={submitting}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="one-tap-modal__body">
          <section className="one-tap-modal__section one-tap-modal__section--toggle">
            <div className="one-tap-modal__toggle-row">
              <div className="one-tap-modal__toggle-copy">
                <h3 className="one-tap-modal__toggle-title">1-Tap schedule</h3>
                <p className="one-tap-modal__hint one-tap-modal__toggle-hint">
                  {enabled
                    ? 'Turn off to pause automatic scheduling from result clips.'
                    : 'Turn on to schedule clips with one tap from your results.'}
                </p>
              </div>
              <label className="one-tap-modal__toggle">
                <input
                  type="checkbox"
                  className="one-tap-modal__toggle-input"
                  checked={enabled}
                  disabled={submitting}
                  onChange={(event) => {
                    setEnabled(event.target.checked);
                    setError('');
                  }}
                />
                <span className="one-tap-modal__toggle-track" aria-hidden="true">
                  <span className="one-tap-modal__toggle-thumb" />
                </span>
                <span className="one-tap-modal__toggle-label">
                  {enabled ? 'On' : 'Off'}
                </span>
              </label>
            </div>
          </section>

          <div className={`one-tap-modal__settings${enabled ? '' : ' is-disabled'}`}>
          <section className="one-tap-modal__section">
            <h3 className="one-tap-modal__section-title">Where do you want to post?</h3>
            <p className="one-tap-modal__hint">Select one or more connected channels.</p>
            <div className="one-tap-modal__platforms">
              {PLATFORMS.map((platform) => {
                const connected = connectedAccounts.some((account) => account.platform === platform.id);
                const selected = selectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    type="button"
                    className={[
                      'one-tap-modal__platform-chip',
                      connected && 'is-connected',
                      selected && 'is-selected',
                      !connected && 'is-disabled',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={!connected || submitting || !enabled}
                    onClick={() => togglePlatform(platform.id)}
                    aria-pressed={selected}
                  >
                    <img
                      src={PLATFORM_ICONS[platform.id]}
                      alt=""
                      width={18}
                      height={18}
                      aria-hidden="true"
                    />
                    <span>{platform.label}</span>
                  </button>
                );
              })}
            </div>
            {connectedAccounts.length === 0 && (
              <p className="one-tap-modal__warn">Connect a platform in the sidebar first.</p>
            )}
          </section>

          <section className="one-tap-modal__section">
            <h3 className="one-tap-modal__section-title">How often per day?</h3>
            <p className="one-tap-modal__hint">Choose how many times you want to post each day.</p>
            <div className="one-tap-modal__frequency" role="group" aria-label="Posts per day">
              {FREQUENCY_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`one-tap-modal__frequency-btn${postsPerDay === option ? ' is-active' : ''}`}
                  disabled={submitting || !enabled}
                  aria-pressed={postsPerDay === option}
                  onClick={() => handleFrequencyChange(option)}
                >
                  {option}× daily
                </button>
              ))}
            </div>
          </section>

          <section className="one-tap-modal__section">
            <h3 className="one-tap-modal__section-title">Pick your time slots</h3>
            <p className="one-tap-modal__hint">
              {postsPerDay === 1
                ? 'Select one time slot.'
                : `Select ${postsPerDay} time slots.${
                    slotsRemaining > 0
                      ? ` ${slotsRemaining} more to go.`
                      : ' All set for this frequency.'
                  }`}
            </p>

            <div className="one-tap-modal__presets">
              {PRESET_TIMES.map((preset) => {
                const selected = timeSlots.includes(preset.value);
                const disabled =
                  submitting
                  || !enabled
                  || (postsPerDay > 1 && !selected && timeSlots.length >= postsPerDay);
                return (
                  <button
                    key={preset.value}
                    type="button"
                    className={`one-tap-modal__time-chip${selected ? ' is-selected' : ''}`}
                    disabled={disabled}
                    aria-pressed={selected}
                    onClick={() => selectPresetTime(preset.value)}
                  >
                    <span className="one-tap-modal__time-chip-text">{preset.label}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className={`one-tap-modal__time-chip one-tap-modal__time-chip--custom${
                  isCustomActive ? ' is-selected' : ''
                }`}
                disabled={submitting || !enabled || (postsPerDay > 1 && !isCustomActive && timeSlots.length >= postsPerDay)}
                aria-pressed={isCustomActive}
                onClick={handleCustomChipClick}
              >
                <span className="one-tap-modal__time-chip-text">Custom</span>
              </button>
            </div>

            {showCustomPicker && (
              <div className="one-tap-modal__custom-time">
                <label className="one-tap-modal__custom-label">
                  <span>Choose time</span>
                  <input
                    type="time"
                    value={customTime}
                    disabled={submitting || !enabled}
                    onChange={(event) => {
                      const nextTime = event.target.value;
                      setCustomTime(nextTime);
                      if (postsPerDay === 1 && nextTime) {
                        setTimeSlots([nextTime]);
                      }
                      setError('');
                    }}
                  />
                </label>
                {postsPerDay > 1 && (
                  <button
                    type="button"
                    className="one-tap-modal__add-time-btn"
                    disabled={submitting || !enabled || !canAddCustomTime}
                    onClick={handleAddCustomTime}
                  >
                    Add
                  </button>
                )}
              </div>
            )}

          </section>
          </div>

          {error && (
            <div className="one-tap-modal__error" role="alert">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="one-tap-modal__success" role="status">
              {successMessage}
            </div>
          )}
        </div>

        <footer className="one-tap-modal__footer">
          <button
            type="button"
            className="one-tap-modal__cancel-btn"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <div className="one-tap-modal__save-wrap">
            <button
              type="button"
              className="one-tap-modal__save-btn"
              disabled={
                submitting
                || (enabled && (selectedPlatforms.length === 0 || timeSlots.length !== postsPerDay))
              }
              onClick={handleSubmit}
            >
              {submitting ? 'Saving…' : enabled ? 'Save preferences' : 'Turn off 1-Tap schedule'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
